import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { isEmptyObject, isStringEmpty, stringEmptyDefault } from '@common/helpers';
import {
  FARE_RULE_TABLE_ITEM_LABEL,
  FARE_RULES_LABEL,
  STAY_PERIOD,
} from '@common/helpers/constants/my-booking/my_booking_itinerary.contant';
import {
  PassengerTypeFareCondition,
  RefundCondition,
} from '@common/interfaces/fare-conditions/fare-conditions-passenger-type.interface';
import { FFPriorityCodeI18nAll } from '@common/interfaces/master/ff_priority_code_i18n_all';
import {
  MasterDataS01P031,
  TravelerFareConditionsResponseData,
} from '@common/interfaces/mybooking-itinerary/mybooking-itinerary.interface';
import { ServicingCommonStoreService } from '@common/services';
import { GetFareConditionsStoreService } from '@common/services/my-booking/get-fare-conditions/get-fare-conditions.service';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { SupportComponent } from '@lib/components/support-class';
import { MOffice } from '@lib/interfaces';
import { MetaUrlPipe, StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { combineLatest, Subscription } from 'rxjs';
import {
  FareConditionsResponseData,
  FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner,
  GetOrderResponseData,
  GetOrderResponseDataAir,
} from 'src/sdk-servicing';

@Component({
  selector: 'asw-fare-rules',
  templateUrl: './fare-rules.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FareRulesComponent extends SupportComponent {
  @Input() functionList!: { [key: string]: Function };
  /** はスクロールです */
  public isScrollFareRules: boolean = false;

  /** 特典種別 */
  labelFareRule!: string;

  /** 運賃ルール詳細表示ボタン */
  itineraryFareRulesDetail!: string;

  /** 注文応答データの取得 */
  orderData!: GetOrderResponseData;

  /** 運賃条件の回答 */
  fareConditions!: FareConditionsResponseData;

  mOffice!: MOffice;
  showFareRules!: boolean;

  /** トラベラーと 運賃条件 */
  travelerFareConditions!: TravelerFareConditionsResponseData;
  airOfferConditionBounds: Array<FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner> = [];
  ffName: string = '';

  readonly MAX_STAY_PERIOD = STAY_PERIOD.MAX;
  readonly MIN_STAY_PERIOD = STAY_PERIOD.MIN;

  private _FFPriorityCodeAll: Array<FFPriorityCodeI18nAll> = [];
  private _subscriptions: Subscription = new Subscription();
  constructor(
    private _common: CommonLibService,
    public _change: ChangeDetectorRef,
    public _myBookingItineraryService: MyBookingItineraryService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _fareConditionsStoreService: GetFareConditionsStoreService,
    private _staticMsg: StaticMsgPipe,
    private _metaUrlPipe: MetaUrlPipe
  ) {
    super(_common);
  }

  init(): void {
    this._subscriptions.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        this.orderData = servicingCommon.getOrder ?? ({} as GetOrderResponseData);
        this._change.markForCheck();
      })
    );
    this._subscriptions.add(
      this._fareConditionsStoreService.getFareConditionsObservable().subscribe((fareCondition) => {
        this.fareConditions = fareCondition.model?.data ?? ({} as FareConditionsResponseData);
        this.airOfferConditionBounds = this.fareConditions.airOfferConditions?.airOfferConditionBounds ?? [];
        this._change.markForCheck();
      })
    );
    this._subscriptions.add(
      this._myBookingItineraryService.masterData$.subscribe((result: MasterDataS01P031) => {
        this._FFPriorityCodeAll = result.fFPriorityCodeAll ?? [];
        this.mOffice = result.mOffice?.[0] ?? ({} as MOffice);
        this._change.markForCheck();
      })
    );
    this._subscriptions.add(
      combineLatest(
        this._fareConditionsStoreService.getFareConditionsObservable(),
        this._servicingCommonStoreService.getServicingCommonModel$()
      ).subscribe(([fareCondition, servicingCommon]) => {
        fareCondition.model?.data;
        /** [1.5.1] 運賃ルール */
        this.showFareRules = this._myBookingItineraryService.showContentModalFareRules(
          servicingCommon?.getOrder,
          fareCondition?.model?.data
        );
        this._change.markForCheck();
      })
    );
  }

  /** Check if table slider is scrolling or not with Fare rules */
  scrollChangeFareRules(value: boolean) {
    this.isScrollFareRules = value;
  }

  /** 運賃規則モーダルを開く */
  openFareRulesModal() {
    if (this.isAwardBooking) {
      this._myBookingItineraryService.openFareRuleOnNewTab(this.isAwardBooking, this.fareConditions);
    } else {
      this.functionList['clickFareRuleDetailsModal']();
    }
  }

  get isAwardBooking() {
    return this.orderData.orderType?.isAwardBooking ?? false;
  }

  /**
   * [1.5.2] 運賃ルール詳細モーダル表示
   * ※PNR情報取得API応答.data.orderType.isAwardBooking = true(特典PNR)の場合、icon_external_primary_16(別タブアイコン)を併記する
   */
  get linkContentLabel(): string {
    return this._myBookingItineraryService.linkContentLabel(this.isAwardBooking, this.fareConditions);
  }

  /** [1.5.3] 運賃ルール情報ラベル */
  get fareFuleInformationLabel() {
    /**
     * 1)PNR情報取得API応答.data.orderType.isAwardBooking=false(有償PNR)の場合、"label.applidRule"(適用運賃)を表示する
     * 2) PNR情報取得API応答.data.orderType.isAwardBooking = true(特典PNR)の場合、"label.fareRule"(特典種別)を表示する
     */
    // 提携: 有償/特典に関わらず同一の文言を表示する。RM#48077
    return FARE_RULES_LABEL.FARE_RULE;
  }

  /** [1.5.4] 運賃ルール情報 */
  get passengerTypeCodes() {
    /** PNR情報取得API応答.data.travelers[]からpassengerTypeCode(搭乗者種別)を全て取得して（重複を除く）搭乗者種別リスト化し、"ADT", "B15", "CHD", "INS", "INF"の順でソートする */
    let passengerTypes = this.orderData?.travelers?.map((m) => m.passengerTypeCode);
    passengerTypes = [...new Set(passengerTypes)];
    let order = ['ADT', 'B15', 'CHD', 'INS', 'INF'];
    passengerTypes.sort((a, b) => order.indexOf(a!) - order.indexOf(b!));
    return passengerTypes;
  }

  /** [1.5.5] 搭乗者種別 */
  passengerType(passengerTypeCode?: string) {
    return this._myBookingItineraryService.getPassengerTypeLabel(passengerTypeCode);
  }

  /** [1.5.7] 出発地 */
  getlocationNameDeparture(bound?: FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner): string {
    /**
     * 1)当該運賃ルール用のバウンド.originLocationCodeを基に「イベントID(1)」で取得した空港リストをフィルタして、空港名称を取得する
     * 2) 取得できない場合は当該運賃ルール用のバウンド.originLocationNameを表示する
     */
    return this._myBookingItineraryService.getAirportLocationName(bound?.originLocationCode, bound?.originLocationName);
  }

  /** [1.5.8] 到着地 */
  getlocationNameArrival(bound?: FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner): string {
    /**
     * 1)当該運賃ルール用のバウンド.destinationLocationCodeを基に「イベントID(1)」で取得した空港リストをフィルタして、空港名称を取得する
     * 2)取得できない場合は当該運賃ルール用のバウンド.destinationLocationNameを表示する
     */
    return this._myBookingItineraryService.getAirportLocationName(
      bound?.destinationLocationCode,
      bound?.destinationLocationName
    );
  }

  /** [1.5.9] FF名称ラベル */
  get setFFNameLabel(): string {
    return this._myBookingItineraryService.getFFNameLabel(this.isAwardBooking);
  }

  /** [1.5.10] FF名称 */
  getFFName(airOfferConditionBound?: FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner) {
    return this._myBookingItineraryService.getFFName(airOfferConditionBound, this.orderData);
  }

  /** [1.5.11] Fare Basis */
  isShowFareBasis(airOfferConditionBound?: FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner) {
    return this.getFFName(airOfferConditionBound) !== this._staticMsg.transform(FARE_RULE_TABLE_ITEM_LABEL.HYPHEN);
  }

  /** [1.5.15] 変更ルール */
  isShowChangeRule() {
    // 提携: UIUXs2では有償のみ表示だが、提携では有償/特典どちらも表示させる。
    return true;
  }

  /** [1.5.15] 変更ルール */
  getChangeRule(passengerTypeCode?: string, boundIndex?: number) {
    const passengerFare = this.fareConditions?.airOfferConditions?.[
      String(passengerTypeCode)
    ] as PassengerTypeFareCondition;
    const defaultDescription =
      passengerFare?.changeConditions?.[boundIndex as number]?.beforeDeparture?.defaultDescription;

    /**
     * 2)PNR情報取得API応答.data.orderType.isAwardBooking=false(有償PNR)の場合、
     * 2.1)運賃ルール・手荷物情報取得API応答.data.airOfferConditions.< 当該搭乗者種別>.changeConditions[当該運賃ルール用のバウンドイン
     * デックス].beforeDeparture.defaultDescription(以下、当該変更ルール)​​≠""(空欄)の場合当該変更ルールを表示する。
     */

    if (this.orderData?.orderType?.isAwardBooking === false) {
      return stringEmptyDefault(defaultDescription);
    } else {
      /**
       * 提携:
       * 2.2)上記以外の場合、特典時の運賃ルールに「運賃ルール詳細をご確認ください」の旨を表示する。
       */
      return this._staticMsg.transform(FARE_RULES_LABEL.AWARD_RULE);
    }
  }

  /** [1.5.16] 払戻ルール */
  isShowRefund() {
    // 提携: UIUXs2では有償のみ表示だが、提携では有償/特典どちらも表示させる。
    return true;
  }

  /** [1.5.16] 払戻ルール */
  getRefundRule(passengerTypeCode?: string, boundIndex?: number) {
    const passengerFare = this.fareConditions?.airOfferConditions?.[
      String(passengerTypeCode)
    ] as PassengerTypeFareCondition;
    const refundConditions = passengerFare?.refundConditions as Array<RefundCondition>;
    const defaultDescription = stringEmptyDefault(
      refundConditions?.[Number(boundIndex)]?.beforeDeparture?.defaultDescription
    );

    /**
     * 2)PNR情報取得API応答.data.orderType.isAwardBooking=false(有償PNR)の場合、
     * 2.1)運賃ルール・手荷物情報取得API応答.data.airOfferConditions.<当該搭乗者種別>.refundConditions[当該運賃ルール用のバウンドインデッ
     * クス].beforeDeparture.defaultDescription(以下、当該払戻ルール)​​≠""(空欄)の当該払戻ルールを表示する。
     */
    if (this.orderData?.orderType?.isAwardBooking === false) {
      return defaultDescription;
    } else {
      /**
       * 提携:
       * 2.2)上記以外の場合、特典時の運賃ルールに「運賃ルール詳細をご確認ください」の旨を表示する。
       */
      return this._staticMsg.transform(FARE_RULES_LABEL.AWARD_RULE);
    }
  }

  /**
   * 提携: airOfferConditionsが未連携の場合、運賃ルールを非表示とする
   */
  get isFareRulesDisplayed(): Boolean {
    return isEmptyObject(this.fareConditions?.airOfferConditions);
  }

  reload(): void {}
  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }
}
